import React, { useState, useMemo, useEffect } from "react";
import { apiFetch, apiFetchBlob, downloadBlob } from "../api";
import { useAuth } from "../store";
import {
  Receipt,
  DollarSign,
  Plus,
  Search,
  X,
  Calendar,
  SlidersHorizontal,
  Pencil,
  Loader2,
  Download,
  PieChart,
  Wallet,
  FileText,
} from "lucide-react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    yellow: "bg-amber-50 text-amber-700 ring-amber-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    red: "bg-rose-50 text-rose-700 ring-rose-100",
    purple: "bg-violet-50 text-violet-700 ring-violet-100",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "neutral" }) {
  const toneColors = {
    neutral: "text-slate-700",
    red: "text-rose-600",
    green: "text-emerald-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className={cx("mt-1 text-xl font-bold", toneColors[tone] || toneColors.neutral)}>
            {value}
          </div>
          {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50"
            aria-label="Fermer"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

function SkeletonTable() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-48 animate-pulse rounded bg-slate-100" />
            </div>
            <div className="h-6 w-24 animate-pulse rounded bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatMoneyXOF(n) {
  const value = Number(n || 0);
  return value.toLocaleString("fr-FR") + " XOF";
}

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null); // expense object being edited
  const [deletingId, setDeletingId] = useState(null); // id being deleted

  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    from: "",
    to: "",
  });

  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "",
    custom_category: "",
    description: "",
    amount: "",
    payment_method: "Espèces",
    recipient_name: "",
    recipient_phone: "",
    recipient_email: "",
    recipient_address: "",
  });

  const { user } = useAuth();
  const connected = !!user;

  const CATEGORIES = [
    "Salaire journalier",
    "Frais administratifs",
    "Salaires",
    "Loyer",
    "Eau / Électricité",
    "Petit matériel",
    "Fournitures de bureau",
    "Entretien et réparations",
    "Sous-traitance",
    "Frais de nettoyage du local",
    "Assurance",
    "Honoraires de consultants",
    "Téléphone / Internet",
    "Publicité / Communication",
    "Frais de transport / Carburant",
    "Autre",
  ];
  const CATEGORY_OTHER = "Autre";

  const PAYMENTS = ["Espèces", "Mobile Money", "Virement / Banque", "Chèque"];

  const categoryColors = {
    "Salaire journalier": "green",
    "Frais administratifs": "neutral",
    Salaires: "green",
    Loyer: "red",
    "Eau / Électricité": "yellow",
    "Petit matériel": "neutral",
    "Fournitures de bureau": "neutral",
    "Entretien et réparations": "purple",
    "Sous-traitance": "blue",
    "Frais de nettoyage du local": "neutral",
    Assurance: "red",
    "Honoraires de consultants": "blue",
    "Téléphone / Internet": "neutral",
    "Publicité / Communication": "blue",
    "Frais de transport / Carburant": "yellow",
    Autre: "neutral",
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchDraft }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, filters.category, filters.from, filters.to]);

  async function loadExpenses() {
    if (!connected) {
      setExpenses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.category) params.set("category", filters.category);

      const data = await apiFetch(`/api/expenses?${params.toString()}`);
      setExpenses(
        data.map((e) => ({
          ...e,
          amount: Number(e.amount || 0),
        }))
      );
    } catch (err) {
      console.warn("Erreur chargement depenses:", err.message);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      date: new Date().toISOString().slice(0, 10),
      category: "",
      custom_category: "",
      description: "",
      amount: "",
      payment_method: "Espèces",
      recipient_name: "",
      recipient_phone: "",
      recipient_email: "",
      recipient_address: "",
    });
  }

  function openCreate() {
    resetForm();
    setEditingExpense(null);
    setShowModal(true);
  }

  function openEdit(expense) {
    const isOther = !CATEGORIES.includes(expense.category) && expense.category !== CATEGORY_OTHER;
    setFormData({
      date: expense.date ? String(expense.date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      category: isOther ? CATEGORY_OTHER : (expense.category || ""),
      custom_category: isOther ? (expense.category || "") : "",
      description: expense.description || "",
      amount: String(Number(expense.amount || 0)),
      payment_method: expense.payment_method || "Espèces",
      recipient_name: expense.recipient_name || "",
      recipient_phone: expense.recipient_phone || "",
      recipient_email: expense.recipient_email || "",
      recipient_address: expense.recipient_address || "",
    });
    setEditingExpense(expense);
    setShowModal(true);
  }

  async function handleDelete(expense) {
    if (!window.confirm(`Supprimer la dépense "${expense.category}" de ${formatMoneyXOF(expense.amount)} ?`)) return;
    setDeletingId(expense.id);
    try {
      await apiFetch(`/api/expenses/${expense.id}`, { method: "DELETE" });
      await loadExpenses();
    } catch (err) {
      alert(err?.message || "Erreur suppression");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.date || !formData.category || !formData.amount) return;
    if (formData.category === CATEGORY_OTHER && !formData.custom_category.trim()) {
      alert("Merci de saisir la categorie personnalisee.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: formData.date,
        category: formData.category,
        customCategory: formData.custom_category?.trim() || null,
        description: formData.description,
        amount: Number(formData.amount),
        payment_method: formData.payment_method,
        recipient_name: formData.recipient_name || null,
        recipient_phone: formData.recipient_phone || null,
        recipient_email: formData.recipient_email || null,
        recipient_address: formData.recipient_address || null,
      };

      if (editingExpense) {
        await apiFetch(`/api/expenses/${editingExpense.id}`, { method: "PATCH", body: payload });
      } else {
        await apiFetch("/api/expenses", { method: "POST", body: payload });
      }

      setShowModal(false);
      setEditingExpense(null);
      resetForm();
      await loadExpenses();
    } catch (err) {
      alert(err?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  }

  async function exportPDF() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.category) params.set("category", filters.category);

      const response = await fetch(`/api/expenses/export?${params.toString()}`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "depenses.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export impossible : " + err.message);
    } finally {
      setExporting(false);
    }
  }

  async function exportExcel() {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.category) params.set("category", filters.category);

      const blob = await apiFetchBlob(`/api/expenses/export.xlsx?${params.toString()}`);
      if (!blob || blob.size === 0) {
        throw new Error("Aucune donnée pour cet export.");
      }
      downloadBlob(blob, `export_depenses_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      alert("Export Excel impossible : " + err.message);
    } finally {
      setExporting(false);
    }
  }

  async function exportExpensePDF(expenseId) {
    try {
      const blob = await apiFetchBlob(`/api/expenses/${expenseId}/pdf`);
      downloadBlob(blob, `depense_${expenseId}.pdf`);
    } catch (err) {
      alert("Export impossible : " + (err?.message || "Erreur"));
    }
  }

  const computed = useMemo(() => {
    const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
    const count = expenses.length;

    const byCategory = {};
    for (const e of expenses) {
      const key = e.category || "Autre";
      byCategory[key] = (byCategory[key] || 0) + Number(e.amount || 0);
    }
    const categories = Object.entries(byCategory)
      .map(([cat, amount]) => ({ cat, amount }))
      .sort((a, b) => b.amount - a.amount);

    const topCategory = categories[0]?.cat || "—";

    return { total, count, categories, topCategory };
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    let list = [...expenses];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(
        (e) =>
          e.category?.toLowerCase().includes(q) ||
          e.description?.toLowerCase().includes(q)
      );
    }

    return list.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [expenses, filters.search]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Receipt className="h-4 w-4" />
                <span>Gestion</span>
                <span className="text-slate-300">/</span>
                <span className="font-medium text-slate-900">Depenses</span>
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Depenses & Charges
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Suivi des charges, filtres par periode/categorie, export PDF.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={exportPDF}
                disabled={exporting || !connected}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {exporting ? "Export..." : "Export PDF"}
              </button>
              <button
                onClick={exportExcel}
                disabled={exporting || !connected}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {exporting ? "Export..." : "Export Excel"}
              </button>
              <button
                onClick={openCreate}
                disabled={!connected}
                className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                Nouvelle depense
              </button>
            </div>
          </div>

          {/* KPI row */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={DollarSign}
              label="Total depenses"
              value={formatMoneyXOF(computed.total)}
              sub="Periode filtree"
              tone="red"
            />
            <KpiCard
              icon={FileText}
              label="Nombre"
              value={computed.count}
              sub="Lignes de depenses"
            />
            <KpiCard
              icon={PieChart}
              label="Categories"
              value={computed.categories.length}
              sub="Differentes"
            />
            <KpiCard
              icon={Wallet}
              label="Top categorie"
              value={computed.topCategory}
              sub="Plus de depenses"
            />
          </div>

          {/* Filters */}
          <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder="Rechercher une depense..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <Calendar className="h-4 w-4 text-slate-500" />
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                  className="bg-transparent text-sm text-slate-900 outline-none"
                />
              </div>

              <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <Calendar className="h-4 w-4 text-slate-500" />
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  className="bg-transparent text-sm text-slate-900 outline-none"
                />
              </div>

              <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                <select
                  value={filters.category}
                  onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
                  className="bg-transparent text-sm text-slate-900 outline-none"
                >
                  <option value="">Toutes categories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 py-6">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Liste des depenses */}
          <div className="xl:col-span-8">
            {loading ? (
              <SkeletonTable />
            ) : filteredExpenses.length === 0 ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                  <Receipt className="h-7 w-7 text-slate-600" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900">
                  Aucune depense trouvee
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Ajuste tes filtres ou cree ta premiere depense.
                </p>
                <button
                  onClick={openCreate}
                  disabled={!connected}
                  className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" />
                  Ajouter une depense
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredExpenses.map((expense) => {
                  const catColor = categoryColors[expense.category] || "neutral";

                  return (
                    <div
                      key={expense.id}
                      className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Pill tone={catColor}>{expense.category}</Pill>
                            <span className="text-xs text-slate-500">{expense.date}</span>
                          </div>
                          <p className="mt-2 text-sm text-slate-700">
                            {expense.description || "Aucune description"}
                          </p>
                          <div className="mt-2 text-xs text-slate-500">
                            Paiement: {expense.payment_method}
                            {expense.recipient_name && (
                              <> · Structure: <span className="font-semibold text-slate-700">{expense.recipient_name}</span></>
                            )}
                            {expense.recipient_phone && (
                              <> · Tél: <span className="font-semibold text-slate-700">{expense.recipient_phone}</span></>
                            )}
                            {expense.recipient_email && (
                              <> · Email: <span className="font-semibold text-slate-700">{expense.recipient_email}</span></>
                            )}
                            {expense.recipient_address && (
                              <> · Adresse: <span className="font-semibold text-slate-700">{expense.recipient_address}</span></>
                            )}
                          </div>
                        </div>

                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold text-rose-600">
                          -{formatMoneyXOF(expense.amount)}
                        </div>
                        <div className="mt-2 flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => exportExpensePDF(expense.id)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          >
                            <Download className="h-3.5 w-3.5" />
                            PDF
                          </button>
                          <button
                            onClick={() => openEdit(expense)}
                            title="Modifier"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDelete(expense)}
                            disabled={deletingId === expense.id}
                            title="Supprimer"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" />
                            {deletingId === expense.id ? "…" : "Supprimer"}
                          </button>
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar: repartition */}
          <div className="xl:col-span-4">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">Repartition par categorie</h3>
                <button
                  onClick={loadExpenses}
                  disabled={loading}
                  className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  {loading ? "..." : "Rafraichir"}
                </button>
              </div>

              {computed.categories.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">Aucune donnee.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {computed.categories.map((c) => {
                    const pct = computed.total > 0 ? (c.amount / computed.total) * 100 : 0;
                    return (
                      <div key={c.cat}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-700">{c.cat}</span>
                          <span className="font-semibold text-slate-900">
                            {formatMoneyXOF(c.amount)}
                          </span>
                        </div>
                        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-slate-900 transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <ModalShell
          title={editingExpense ? "Modifier la dépense" : "Nouvelle depense"}
          subtitle={editingExpense ? `Modification de ${formatMoneyXOF(editingExpense.amount)}` : "Enregistre une charge ou depense"}
          onClose={() => { setShowModal(false); setEditingExpense(null); resetForm(); }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-900">Date *</label>
                <input
                  required
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Categorie *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      category: next,
                      custom_category: next === CATEGORY_OTHER ? prev.custom_category : "",
                    }));
                  }}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="">Selectionner</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <div
                  className={cx(
                    "mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-4 transition-all duration-200",
                    formData.category === CATEGORY_OTHER
                      ? "max-h-40 opacity-100 py-3"
                      : "max-h-0 opacity-0 py-0 border-transparent"
                  )}
                  aria-hidden={formData.category !== CATEGORY_OTHER}
                >
                  <label className="text-sm font-semibold text-slate-900">
                    Categorie personnalisee *
                  </label>
                  <input
                    required={formData.category === CATEGORY_OTHER}
                    disabled={formData.category !== CATEGORY_OTHER}
                    value={formData.custom_category}
                    onChange={(e) =>
                      setFormData({ ...formData, custom_category: e.target.value })
                    }
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100 disabled:bg-slate-50"
                    placeholder="Ex: Paiement employe, frais exceptionnels..."
                  />
                  <div className="mt-2 text-xs text-slate-600">
                    Utilisez cette option si votre depense ne correspond a aucune categorie
                    existante.
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold text-slate-900">Montant (XOF) *</label>
                <input
                  required
                  type="number"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-900">Mode paiement</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  {PAYMENTS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-900">Description</label>
              <input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                placeholder="Ex: Taxi pour livraison, carburant..."
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-900">Structure / Client</label>
              <input
                value={formData.recipient_name}
                onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                placeholder="Nom de la structure ou du client..."
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="text-sm font-semibold text-slate-900">Téléphone</label>
                <input
                  value={formData.recipient_phone}
                  onChange={(e) => setFormData({ ...formData, recipient_phone: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="Numéro de téléphone..."
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-900">Email</label>
                <input
                  type="email"
                  value={formData.recipient_email}
                  onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="Email..."
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-900">Adresse</label>
                <input
                  value={formData.recipient_address}
                  onChange={(e) => setFormData({ ...formData, recipient_address: e.target.value })}
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="Adresse physique..."
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="flex-1 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
              >
                {saving ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enregistrement...
                  </span>
                ) : (
                  editingExpense ? "Mettre à jour" : "Enregistrer"
                )}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
}
